import React from 'react';
import { Box, Button, CircularProgress, MenuItem, Select, Stack, Typography } from '@mui/material';
import ArrowBackIosNewRoundedIcon from '@mui/icons-material/ArrowBackIosNewRounded';
import SaveRoundedIcon from '@mui/icons-material/SaveRounded';
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import BarChartRoundedIcon from '@mui/icons-material/BarChartRounded';
import PrintOutlinedIcon from '@mui/icons-material/PrintOutlined';

type NutritionalAnalysisTopBarProps = {
  selectedDayNumber: number;
  availableDayNumbers: number[];
  dayNamesByNumber?: Record<number, string>;
  onDayChange: (dayNumber: number) => void;
  onBackClick: () => void;
  totalCaloriesLabel: string;
  showTotalCaloriesButton?: boolean;
  showSaveButton?: boolean;
  onSaveChanges?: () => void;
  saveChangesDisabled?: boolean;
  saveChangesLabel?: string;
  /** Defaults to "Back to Day Plan"; parent may pass "Back" for generic meal plan views. */
  backLabel?: string;
  /** When false, hides the day selector dropdown (used in template detail views). */
  showDaySelector?: boolean;
  highlightDaySelector?: boolean;
  /** Optional plain label to show instead of dropdown (used in generic meal plan view). */
  dayDisplayLabel?: string;
  /** Day note control, shown next to the day selector. */
  dayNoteAction?: React.ReactNode;
  /** Plan note control, shown with the right-side actions. */
  planNoteAction?: React.ReactNode;
  /** Plan-level PDF export, shown with Save as a share action. */
  showExportPdf?: boolean;
  isExportingPdf?: boolean;
  exportPdfDisabled?: boolean;
  onExportPdf?: () => void;
};

const NutritionalAnalysisTopBar: React.FC<NutritionalAnalysisTopBarProps> = ({
  selectedDayNumber,
  availableDayNumbers,
  dayNamesByNumber,
  onDayChange,
  onBackClick,
  totalCaloriesLabel,
  showTotalCaloriesButton = true,
  showSaveButton = true,
  onSaveChanges,
  saveChangesDisabled = false,
  saveChangesLabel = 'Save Changes',
  backLabel = 'Back to Day Plan',
  showDaySelector = true,
  highlightDaySelector = false,
  dayDisplayLabel,
  dayNoteAction,
  planNoteAction,
  showExportPdf = false,
  isExportingPdf = false,
  exportPdfDisabled = false,
  onExportPdf,
}) => {
  const resolveDayLabel = (dayNumber: number) => dayNamesByNumber?.[dayNumber] ?? `Day ${dayNumber}`;

  return (
    <Box
      sx={{
        px: '16px',
        pt: '16px',
        pb: '17.333px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 2,
        flexWrap: 'wrap',
        borderBottom: '1.333px solid #E5E7EB',
      }}
    >
      <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
        <Button
          onClick={onBackClick}
          variant="text"
          sx={{
            color: '#6A7282',
            textTransform: 'none',
            fontSize: '14px',
            lineHeight: '20px',
            fontWeight: 500,
            minHeight: '34.667px',
            px: 0,
            minWidth: 0,
            border: 'none',
            bgcolor: 'transparent',
            '&:hover': { bgcolor: 'transparent' },
          }}
        >
          <ArrowBackIosNewRoundedIcon sx={{ fontSize: 16, mr: '3px' }} />
          {backLabel}
        </Button>
        {showDaySelector ? (
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 1,
              border: '1px solid transparent',
              borderRadius: '12px',
              px: '6px',
              py: '4px',
              mx: '-6px',
              my: '-4px',
              bgcolor: highlightDaySelector ? 'rgba(32, 174, 220, 0.12)' : 'transparent',
              boxShadow: highlightDaySelector ? '0 0 0 4px rgba(32, 174, 220, 0.22)' : 'none',
              borderColor: highlightDaySelector ? '#20AEDC' : 'transparent',
              transition: 'background-color 180ms ease, box-shadow 180ms ease, border-color 180ms ease',
            }}
          >
            <Typography sx={{ fontSize: '16px', lineHeight: '24px', color: '#D1D5DC' }}>|</Typography>
            <Select
              size="small"
              value={String(selectedDayNumber)}
              onChange={(event) => onDayChange(Number(event.target.value) || 1)}
              IconComponent={KeyboardArrowDownRoundedIcon}
              sx={{
                minHeight: '34.667px',
                borderRadius: '8px',
                color: '#4A5565',
                fontSize: '14px',
                fontWeight: 400,
                lineHeight: '20px',
                '& .MuiOutlinedInput-notchedOutline': { borderColor: '#E5E7EB' },
                '& .MuiSelect-select': {
                  py: '6px',
                  px: '11px',
                  pr: '30px !important',
                },
              }}
            >
              {availableDayNumbers.map((dayNumber) => (
                <MenuItem key={dayNumber} value={String(dayNumber)}>
                  {resolveDayLabel(dayNumber)}
                </MenuItem>
              ))}
            </Select>
            {dayNoteAction ? (
              <Box sx={{ display: 'inline-flex', alignItems: 'center' }}>{dayNoteAction}</Box>
            ) : null}
          </Box>
        ) : dayDisplayLabel ? (
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography sx={{ fontSize: '16px', lineHeight: '24px', color: '#D1D5DC' }}>|</Typography>
            <Typography
              sx={{
                color: '#4A5565',
                fontSize: '14px',
                fontWeight: 500,
                lineHeight: '20px',
              }}
            >
              {dayDisplayLabel}
            </Typography>
            {dayNoteAction ? (
              <Box sx={{ display: 'inline-flex', alignItems: 'center' }}>{dayNoteAction}</Box>
            ) : null}
          </Stack>
        ) : dayNoteAction ? (
          <Box sx={{ display: 'inline-flex', alignItems: 'center' }}>{dayNoteAction}</Box>
        ) : null}
      </Stack>

      <Stack direction="row" spacing={1.25} alignItems="center" flexWrap="wrap">
        {planNoteAction ? (
          <Box sx={{ display: 'inline-flex', alignItems: 'center' }}>{planNoteAction}</Box>
        ) : null}
        {showTotalCaloriesButton ? (
          <Button
            variant="outlined"
            startIcon={<BarChartRoundedIcon sx={{ fontSize: 16 }} />}
            sx={{
              borderColor: '#E8F4FF',
              bgcolor: '#E8F4FF',
              color: '#0891B2',
              textTransform: 'none',
              fontSize: '14px',
              fontWeight: 600,
              lineHeight: '21px',
              height: '36px',
              px: '12px',
              borderRadius: '8px',
            }}
          >
            {totalCaloriesLabel} kcal
          </Button>
        ) : null}
        {showExportPdf ? (
          <Button
            variant="outlined"
            startIcon={
              isExportingPdf ? (
                <CircularProgress size={14} thickness={5} sx={{ color: 'currentColor' }} />
              ) : (
                <PrintOutlinedIcon sx={{ fontSize: 16 }} />
              )
            }
            onClick={onExportPdf}
            disabled={isExportingPdf || exportPdfDisabled}
            sx={{
              borderColor: isExportingPdf ? '#20AEDC' : '#E5E7EB',
              bgcolor: isExportingPdf ? '#EAF7FC' : '#fff',
              color: isExportingPdf ? '#127EA5' : '#4A5565',
              textTransform: 'none',
              fontSize: '14px',
              fontWeight: 500,
              lineHeight: '20px',
              height: '36px',
              px: '12px',
              borderRadius: '8px',
              '&:hover': {
                borderColor: '#20AEDC',
                bgcolor: '#EAF7FC',
              },
              '&.Mui-disabled': isExportingPdf
                ? {
                    borderColor: '#20AEDC',
                    bgcolor: '#EAF7FC',
                    color: '#127EA5',
                  }
                : {
                    borderColor: '#E5E7EB',
                    bgcolor: '#F8FAFC',
                    color: '#98A2B3',
                  },
            }}
          >
            {isExportingPdf ? 'Exporting...' : 'Export PDF'}
          </Button>
        ) : null}
        {showSaveButton ? (
          <Button
            variant="contained"
            startIcon={<SaveRoundedIcon sx={{ fontSize: 14 }} />}
            onClick={onSaveChanges}
            disabled={saveChangesDisabled}
            sx={{
              textTransform: 'none',
              fontSize: '14px',
              fontWeight: 500,
              lineHeight: '20px',
              height: '36px',
              px: '11px',
              borderRadius: '8px',
              bgcolor: '#20AEDC',
              '&:hover': { bgcolor: '#20AEDC' },
            }}
          >
            {saveChangesLabel}
          </Button>
        ) : null}
      </Stack>
    </Box>
  );
};

export default NutritionalAnalysisTopBar;
